// ```calc blocks stay text to type in, each line's result drawn at its right
// edge, CalcPad style. The lines get room on the right for the block's widest
// result, so what's typed never runs under one. The line the cursor is on
// is highlighted, as CalcPad does.
import {
  findCalcs,
  results,
} from '@/app/calc/calc';
import {
  type EditorState,
  StateField,
} from '@codemirror/state';
import {
  Decoration,
  type DecorationSet,
  EditorView,
  WidgetType,
} from '@codemirror/view';

class ResultWidget extends WidgetType {
  constructor(readonly result: string) {
    super();
  }

  override eq(other: ResultWidget) {
    return other.result === this.result;
  }

  toDOM() {
    const dom = document.createElement('span');
    dom.className = 'calc-result';
    dom.textContent = this.result;
    return dom;
  }
}

const fence = Decoration.line({ class: 'calc-fence' });

const decorate = (state: EditorState): DecorationSet => {
  const { doc } = state;
  return Decoration.set(
    findCalcs(doc.toString()).flatMap(block => {
      const first = doc.lineAt(block.contentFrom).number;
      const last = doc.lineAt(block.to).number - 1;
      const lines = Array.from(
        { length: last - first + 1 },
        (_, i) => doc.line(first + i),
      );
      const shown = results(block.content);
      const widest = Math.max(0, ...shown.map(r => r.length));
      const line = Decoration.line({
        class: 'calc-line',
        attributes: { style: `--calc-width: ${widest}ch` },
      });
      return [
        fence.range(block.from),
        ...lines.flatMap((l, i) => [
          line.range(l.from),
          ...shown[i]
            ? [
              Decoration.widget({ widget: new ResultWidget(shown[i]), side: 1 })
                .range(l.to),
            ]
            : [],
        ]),
        fence.range(doc.lineAt(block.to).from),
      ];
    }),
    true,
  );
};

const shown = StateField.define<DecorationSet>({
  create: decorate,
  update: (set, tr) => tr.docChanged ? decorate(tr.state) : set,
  provide: field => EditorView.decorations.from(field),
});

const isCalcLine = (state: EditorState, from: number) => {
  let found = false;
  state.field(shown).between(from, from, (at, _, deco) => {
    found ||= at === from && deco.spec.class === 'calc-line';
  });
  return found;
};

const current = Decoration.line({ class: 'calc-current' });

// apart from the results, so moving the cursor doesn't evaluate again
const highlight = (state: EditorState): DecorationSet =>
  Decoration.set(
    [...new Set(state.selection.ranges.map(r => state.doc.lineAt(r.head).from))]
      .filter(from => isCalcLine(state, from))
      .map(from => current.range(from)),
    true,
  );

const currentLine = StateField.define<DecorationSet>({
  create: highlight,
  update: (set, tr) =>
    tr.docChanged || tr.selection ? highlight(tr.state) : set,
  provide: field => EditorView.decorations.from(field),
});

export const calcs = [shown, currentLine];

export const newCalc = [
  '```calc',
  'price = 12.50',
  'total = price * 3',
  '10% off total',
  '```',
  '',
].join('\n');
